import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { noop } from 'lodash';
import { View } from 'react-native';
import Animated, { useSharedValue, useAnimatedScrollHandler, } from 'react-native-reanimated';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import WebTabs from 'design-system/WebTabs.web';
import { AppView } from 'design-system/AppView';
import StatusBar from 'design-system/StatusBar';
import IconPlus from 'design-system/icons/IconPlus';
import Button, { HEIGHT } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import AccountsTopSync from 'features/settings/logins/AccountsTopSync';
import useAccountCallbacks from 'hooks/useAccountCallbacks';
import useColors from 'hooks/useColors';
import { selectAccountsSeparatedDebt } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { getAccountGoals } from 'actions';
import useFetchCreditReportForDebtTracking from 'features/credit-score/hooks/useFetchCreditReportForDebtTracking';
import { useAddAccountModal } from '../hooks/useAddAccountModal';
import AccountsHeaderGraph from '../components/AccountsHeaderGraph';
import AccountsOverviewSection from '../components/overview/AccountsOverviewSection';
import { accountOverviewScreenTitleToSelectedAccountTitleMap, isAccountOverviewScreenTitle, } from '../types';
import NetWorthMainSection from '../components/overview/NetWorthMainSection';
import NetWorthAssetsSection from '../components/overview/NetWorthAssetsSection';
import NetWorthDebtSection from '../components/overview/NetWorthDebtSection';
const titles = [
    'Everyday',
    'Savings',
    'Investments',
    'Net Worth - Main',
];
const netWorthTitles = ['Net worth', 'Assets', 'Debt'];
const netWorthTitleMap = {
    'Net worth': 'Net Worth - Main',
    Assets: 'Net Worth - Asset',
    Debt: 'Net Worth - Debt',
};
const AccountsOverviewScreen = withAnimatedNavigationHeader(({ navigation, route, scrollY }) => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (!store.getState().accounts.loadedGoals) {
            dispatch(getAccountGoals());
        }
    }, []);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const scrollX = useSharedValue(0);
    const accounts = useAppSelector(selectAccountsSeparatedDebt);
    useFetchCreditReportForDebtTracking();
    const { onRowPress, onOpenLogins, onOpenUpgrade } = useAccountCallbacks(false);
    const initialTitle = isAccountOverviewScreenTitle(route.params?.title)
        ? route.params.title
        : 'Everyday';
    const scrollRef = useRef(null);
    const [selectedTab, setSelectedTab] = useState(initialTitle);
    useEffect(() => {
        if (route.name === 'NetworthTab' && initialTitle !== selectedTab) {
            scrollRef.current?.scrollTo({ y: 0, animated: false });
            setSelectedTab(initialTitle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTitle]);
    const addAccount = useAddAccountModal(accountOverviewScreenTitleToSelectedAccountTitleMap[selectedTab]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => (<View style={styles.iconContainer}>
            <NavigationHeaderRightIconButton style={styles.addAccountIconBtn} onPress={addAccount}>
              <IconPlus color={colors.text.primary}/>
            </NavigationHeaderRightIconButton>
            <AccountsTopSync />
            <Spacer w={16}/>
          </View>),
        });
    }, [
        addAccount,
        colors.text.primary,
        scrollX,
        styles.addAccountIconBtn,
        styles.iconContainer,
    ]);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'netWorth',
    });
    const onScroll = useAnimatedScrollHandler((event) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = event.contentOffset.y;
    });
    // Handle tab changes
    const handleTabChange = useCallback((tab) => {
        setSelectedTab(tab);
        navigation.setParams({
            title: tab,
        });
    }, []);
    const header = useMemo(() => (<AccountsHeaderGraph scrollX={scrollX} title={selectedTab} blurValues={!isUnlockedForSpace} onPressUpgrade={onPressUpgrade}/>), [isUnlockedForSpace, onPressUpgrade, scrollX, selectedTab]);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(24));
    const onPressAssets = useCallback(() => {
        setSelectedTab('Net Worth - Asset');
    }, []);
    const onPressDebt = useCallback(() => {
        setSelectedTab('Net Worth - Debt');
    }, []);
    const content = useMemo(() => {
        const item = selectedTab;
        if (item === 'Net Worth - Main') {
            return (<NetWorthMainSection onPressAssets={onPressAssets} onPressDebt={onPressDebt} blurValues={!isUnlockedForSpace}/>);
        }
        if (item === 'Net Worth - Asset') {
            return (<NetWorthAssetsSection accounts={accounts} onRowPress={onRowPress} onOpenLogins={onOpenLogins} onAddAccount={noop} openAlternative={noop} onOpenUpgrade={onOpenUpgrade} onRowLongPress={noop} isUnlockedForSpace={isUnlockedForSpace}/>);
        }
        if (item === 'Net Worth - Debt') {
            return (<NetWorthDebtSection accounts={accounts} onRowPress={onRowPress} onOpenLogins={onOpenLogins} onAddAccount={noop} openAlternative={noop} onOpenUpgrade={onOpenUpgrade} onRowLongPress={noop} isUnlockedForSpace={isUnlockedForSpace}/>);
        }
        return (<AccountsOverviewSection title={item} accounts={accounts} onRowPress={onRowPress} initialTitle={initialTitle} onOpenLogins={onOpenLogins} onOpenUpgrade={onOpenUpgrade} onRowLongPress={noop}/>);
    }, [
        selectedTab,
        accounts,
        onRowPress,
        initialTitle,
        onOpenLogins,
        onOpenUpgrade,
        onPressAssets,
        onPressDebt,
        isUnlockedForSpace,
    ]);
    return (<AppView style={styles.container} isInTab>
        <View style={styles.innerContainer}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView ref={scrollRef} snapToEnd={false} onScroll={onScroll} decelerationRate="fast" scrollEventThrottle={16} showsVerticalScrollIndicator={false} contentContainerStyle={paddingBottom}>
            <WebTabs tabs={titles} displayTabs={titles.map((t) => accountOverviewScreenTitleToSelectedAccountTitleMap[t])} activeTab={selectedTab === 'Net Worth - Debt' ||
            selectedTab === 'Net Worth - Asset'
            ? 'Net Worth - Main'
            : selectedTab} handleTabChange={handleTabChange}/>
            {header}
            {(selectedTab === 'Net Worth - Main' ||
            selectedTab === 'Net Worth - Asset' ||
            selectedTab === 'Net Worth - Debt') && (<View style={styles.netWorthPageControlContainer}>
                <WebTabs tabs={netWorthTitles} activeTab={{
                'Net Worth - Main': 'Net worth',
                'Net Worth - Asset': 'Assets',
                'Net Worth - Debt': 'Debt',
            }[selectedTab]} handleTabChange={(title) => {
                const tab = netWorthTitleMap[title];
                if (tab) {
                    handleTabChange(tab);
                }
            }}/>
              </View>)}
            {content}
          </Animated.ScrollView>
        </View>
        <Button brand floating preventDoubleTap title="Add account" onPress={addAccount}/>
      </AppView>);
}, ({ colors, route }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    title: isAccountOverviewScreenTitle(route.params?.title)
        ? accountOverviewScreenTitleToSelectedAccountTitleMap[route.params.title]
        : undefined,
}));
const styleSet = createStyleSheets(() => ({
    container: {
        overflow: 'hidden',
    },
    innerContainer: {
        flex: 1,
        overflow: 'hidden',
    },
    iconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    netWorthPageControlContainer: {
        marginBottom: rem(16),
    },
    addAccountIconBtn: {
        marginRight: rem(8),
    },
}));
export default AccountsOverviewScreen;
